import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import _ from 'lodash';

import {
    DefaultButton,
    PrimaryButton,
    Panel,
    PanelType,
    Separator,
    Stack,
    IconButton,
} from '@fluentui/react';

import { CustomView, CustomViewTimePeriodEnum, TicketStatusEnum } from '../../../../../api-client';
import { TRANSLATION_KEYS } from './localization/translation-keys';

import { FormProvider, IAIComboBox, IAITextField } from '../../../../../common/components/IAIForm';
import IAICheckboxField from '../../../../../common/components/IAIForm/IAICheckBox/iai-checkbox';

interface FormValuesProps extends CustomView { }

interface IFormDrawer {
    customView?: CustomView;
    disabled?: boolean;
    drawerVisible?: boolean;
    onDrawerConfirm: (dataItem: CustomView) => void;
    onDrawerDismiss: () => void;
}

const FormDrawer: React.FunctionComponent<IFormDrawer> = ({
    customView,
    disabled,
    drawerVisible,
    onDrawerConfirm,
    onDrawerDismiss
}) => {
    const {
        t: translate
    } = useTranslation();

    //#region Form-Configuration

    const formDataSchema = Yup.object().shape({
        displayName: Yup.string().required(translate(TRANSLATION_KEYS.ViewNameErrorMessage)),
        status: Yup.array().of(Yup.string()).optional(),
        selfAssigned: Yup.bool(),
        timePeriod: Yup.string().optional(),
    });

    const defaultValues = useMemo(
        () => ({
            displayName: customView?.displayName ?? '',
            status: _.map(customView?.status) ?? [],
            selfAssigned: customView?.selfAssigned ?? false,
            timePeriod: customView?.timePeriod ?? '',
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [customView]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        reset,
        handleSubmit,
        formState: { isSubmitting },
        setValue,
    } = methods;

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customView]);

    //#endregion

    const _handleDismiss = () => {
        reset(defaultValues);
        onDrawerDismiss();
    }

    const _handleSubmit = (data: FormValuesProps) => {
        let customViewData = {
            ...customView,
            ...data,
        };

        onDrawerConfirm(customViewData);
    }

    const disableCondition = disabled || isSubmitting;

    return (
        <FormProvider methods={methods}>
            <Panel
                type={PanelType.medium}
                headerText={translate(TRANSLATION_KEYS.Heading)}
                isOpen={drawerVisible}
                isBlocking={true}
                isFooterAtBottom={true}
                onDismiss={_handleDismiss}
                onRenderFooterContent={() => (
                    <div className="text-end">
                        <DefaultButton
                            disabled={disableCondition}
                            iconProps={{ iconName: 'Cancel' }}
                            styles={{ root: { marginRight: 10 } }}
                            onClick={_handleDismiss}
                        >
                            {translate(TRANSLATION_KEYS.DismissText)}
                        </DefaultButton>
                        <PrimaryButton
                            type="submit"
                            disabled={disableCondition}
                            iconProps={{ iconName: 'CheckMark' }}
                            onClick={handleSubmit(_handleSubmit)}
                        >
                            {translate(TRANSLATION_KEYS.ConfirmText)}
                        </PrimaryButton>
                    </div>
                )}
            >
                <Separator />

                <Stack tokens={{ childrenGap: 15 }}>
                    <IAITextField
                        name="displayName"
                        label={translate(TRANSLATION_KEYS.ViewNameLabel)}
                        placeholder={translate(TRANSLATION_KEYS.ViewNamePlaceholder)}
                        required
                        disabled={disableCondition}
                    />

                    <IAICheckboxField
                        name="selfAssigned"
                        label={translate(TRANSLATION_KEYS.AssignToLabel)}
                    />


                    <IAIComboBox
                        name="status"
                        label={translate(TRANSLATION_KEYS.StatusLabel)}
                        placeholder={translate(TRANSLATION_KEYS.StatusPlaceholder)}
                        autoComplete="on"
                        multiSelect
                        options={[
                            { key: TicketStatusEnum.Canceled, text: translate(TRANSLATION_KEYS.StatusOptionCanceled) },
                            { key: TicketStatusEnum.Closed, text: translate(TRANSLATION_KEYS.StatusOptionClosed) },
                            { key: TicketStatusEnum.Deleted, text: translate(TRANSLATION_KEYS.StatusOptionDeleted) },
                            { key: TicketStatusEnum.Open, text: translate(TRANSLATION_KEYS.StatusOptionOpen) },
                            { key: TicketStatusEnum.Pending, text: translate(TRANSLATION_KEYS.StatusOptionPending) },
                            { key: TicketStatusEnum.Working, text: translate(TRANSLATION_KEYS.StatusOptionWorking) },
                        ]}
                    />

                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                        <Stack.Item grow>
                            <IAIComboBox
                                name="timePeriod"
                                label={translate(TRANSLATION_KEYS.TimePeriodLabel)}
                                placeholder={translate(TRANSLATION_KEYS.TimePeriodPlaceholder)}
                                autoComplete="on"
                                options={[
                                    { key: CustomViewTimePeriodEnum.CurrentWeek, text: translate(TRANSLATION_KEYS.TimePeriodOptionCurrentWeek) },
                                    { key: CustomViewTimePeriodEnum.CurrentMonth, text: translate(TRANSLATION_KEYS.TimePeriodOptionCurrentMonth) },
                                    { key: CustomViewTimePeriodEnum.Last7Days, text: translate(TRANSLATION_KEYS.TimePeriodOptionLast7Days) },
                                    { key: CustomViewTimePeriodEnum.Last30Days, text: translate(TRANSLATION_KEYS.TimePeriodOptionLast30Days) },
                                ]}
                            />
                        </Stack.Item>

                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            aria-label="Clear the time period input"
                            onClick={() => setValue("timePeriod", "")}
                        />
                    </Stack>
                </Stack>
            </Panel>
        </FormProvider>
    );
}

export default FormDrawer;